import { Injectable } from '@angular/core';
import { Row } from '../../models/analytics-api/query_result.model';
import { DatesService } from '../../modules/date-range-picker/dates.service';
import { DateRange, DateRangeValue, DynamicRange } from '../../modules/date-range-picker/types';

@Injectable()
export class BenchmarkService {
  constructor(
    private datesService: DatesService) { }

  calculateBenchmark(dateInterval: DateRangeValue, benchmark: string, queryFirstDayOfWeek: number = undefined) {
    let dates = {};
    if (dateInterval) {
      switch (benchmark) {
        case 'previous_period':
          dates = this.previousPeriod(dateInterval, queryFirstDayOfWeek);
          break;
        case 'previous_year':
          dates = this.previousYear(dateInterval, queryFirstDayOfWeek);
          break;
      }
    }
    return Object.add(benchmark, dates);
  }

  createBenchmarkPages(rows, benchmarks, resultsPerPage, transpose = 'rows') {
    return benchmarks
      .map((b, index) => {
        if (b) {
          const row = rows[index];
          return row.metrics.reduce((memo, metric) => {
            const n = metric.name;
            let diff;
            if (transpose === 'columns') {
              diff = metric.values.map((value, index) => this.diff(value, b[n][index]));
            } else {
              diff = this.diff(metric.value, b[n]);
            }
            memo[n] = diff;
            return memo;
          }, {});
        } else {
          return {};
        }
      })
      .inGroupsOf(resultsPerPage)
      .map('compact');
  }

  getSortedUniqueDatesFromRows(rows: Row[]): string[] {
    return rows
      .reduce(
        (unique, row) =>
          unique.includes(row.date.getTime().toString())
            ? unique
            : [...unique, row.date.getTime().toString()],
        [],
      )
      .sort((dateA, dateB) => dateA - dateB);
  }

  public diff(value: number, benchmark: number): number {
    if (value !== null && benchmark > 0) {
      let diff = value / benchmark;
      diff -= 1;
      return diff;
    }
  }

  private previousPeriod(dateInterval: DateRangeValue, queryFirstDayOfWeek: number) {
    const isDynamic = dateInterval.type === 'dynamic' || dateInterval['dynamic'];
    if (isDynamic) {
      return this.calculateDynamicPreviousPeriod(dateInterval, queryFirstDayOfWeek);
    } else {
      return this.calculateFixedPreviousPeriod(dateInterval, queryFirstDayOfWeek);
    }
  }

  private calculateDynamicPreviousPeriod(dateInterval: DateRangeValue, queryFirstDayOfWeek: number) {
    const base = this.datesService.parseValue(dateInterval, queryFirstDayOfWeek);
    const dynamic = dateInterval['dynamic'];

    if (dynamic.name === 'yesterday') {
      const yesterday = base.startDate.clone().addDays(-1);
      return {
        startDate: yesterday,
        endDate: yesterday,
      };
    } else {
      const offsetFn = `add${dynamic.unit.titleize()}s`;
      const startDate = base.startDate.clone()[offsetFn](-dynamic.num);
      let endDate = base.startDate.clone().addDays(-1);

      if (dynamic.toDate && this.supportsToDate(dynamic.name, dynamic.unit)) {
        endDate = this.adjustEndDateForDynamic(base, startDate, endDate, dynamic.unit);
      }

      return {
        startDate,
        endDate,
      };
    }
  }

  private supportsToDate(name: string, unit: string): boolean {
    return (name === 'this' && (unit === 'month' || unit === 'quarter' || unit === 'year'));
  }

  private adjustEndDateForDynamic(base: DateRange, startDate: Date, endDate: Date, unit: string) {
    if (unit === 'month' || unit === 'quarter') {
      const monthsBetween = base.endDate.getMonth() - base.startDate.getMonth();
      endDate = unit === 'quarter' ? startDate.clone().addMonths(monthsBetween).beginningOfMonth() : endDate.clone();
      const endOfMonth = endDate.clone().endOfMonth().getDate();
      endDate.set({ date: Math.min(base.endDate.clone().getDate(), endOfMonth) });
    } else {
      endDate.set({ month: base.endDate.getMonth(), date: base.endDate.getDate() });
    }
    return endDate;
  }

  private calculateFixedPreviousPeriod(dateInterval: DateRangeValue, queryFirstDayOfWeek: number) {
    const dates = this.datesService.parseValue(dateInterval, queryFirstDayOfWeek);
    const daysBetween = dates.startDate.daysUntil(dates.endDate) + 1;
    return {
      startDate: dates.startDate.clone().addDays(-daysBetween),
      endDate: dates.endDate.clone().addDays(-daysBetween),
    };
  }

  private previousYear(dateInterval: DateRangeValue, queryFirstDayOfWeek: number): DateRange {
    const dates = this.datesService.parseValue(dateInterval, queryFirstDayOfWeek);
    const startDate = new Date(dates.startDate.getTime());
    const endDate = new Date(dates.endDate.getTime());
    const yearsSpan = endDate.getFullYear() - startDate.getFullYear();

    endDate.setFullYear(endDate.getFullYear() - 1);
    startDate.setFullYear(endDate.getFullYear() - yearsSpan);

    return { startDate, endDate };
  }
}
